import { Injectable, Inject, PLATFORM_ID } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { KeyboardAction, KeyboardSequenceStep } from './tour.types';

@Injectable({
  providedIn: 'root'
})
export class TourKeyboardAutomationService {
  private isMac: boolean;
  private isBrowser: boolean;

  constructor(@Inject(PLATFORM_ID) private platformId: Object) {
    this.isBrowser = isPlatformBrowser(platformId);
    this.isMac = this.isBrowser && navigator.platform.toUpperCase().indexOf('MAC') >= 0;
  }

  /**
   * Execute a keyboard automation sequence
   */
  async executeKeyboardSequence(action: KeyboardAction): Promise<void> {
    // Skip if not in browser
    if (!this.isBrowser) {
      console.log('[TourKeyboardAutomation] Skipping - not in browser');
      return;
    }

    // Check platform compatibility
    if (action.platform && action.platform !== 'both') {
      const shouldRun = (action.platform === 'mac' && this.isMac) || 
                       (action.platform === 'windows' && !this.isMac);
      if (!shouldRun) {
        console.log('[TourKeyboardAutomation] Skipping - platform mismatch');
        return;
      }
    }

    console.log('[TourKeyboardAutomation] Starting sequence with', action.sequence.length, 'steps');

    for (const step of action.sequence) {
      // Wait before executing this step
      if (step.delay) {
        await this.wait(step.delay);
      }

      switch (step.action) {
        case 'shortcut':
          await this.triggerShortcut(step.keys || []);
          break;
        case 'type':
          await this.typeText(step.text || '');
          break;
        case 'wait':
          await this.wait(step.delay || 0);
          break;
      }
    }

    console.log('[TourKeyboardAutomation] Sequence complete');
  }

  /**
   * Trigger a keyboard shortcut
   */
  private async triggerShortcut(keys: string[]): Promise<void> {
    if (keys.length === 0) return;

    console.log('[TourKeyboardAutomation] Triggering shortcut:', keys.join('+'));

    const activeElement = document.activeElement as HTMLElement;
    const keyEvent: KeyboardEventInit = {
      bubbles: true,
      cancelable: true,
      composed: true
    };

    // Parse key modifiers and main key
    const modifiers = {
      ctrlKey: false,
      altKey: false,
      shiftKey: false,
      metaKey: false
    };

    let mainKey = '';

    for (const key of keys) {
      const keyLower = key.toLowerCase();
      
      if (keyLower === 'ctrl' || keyLower === 'control') {
        modifiers.ctrlKey = true;
      } else if (keyLower === 'alt' || keyLower === 'option') {
        modifiers.altKey = true;
      } else if (keyLower === 'shift') {
        modifiers.shiftKey = true;
      } else if (keyLower === 'cmd' || keyLower === 'command' || keyLower === 'meta') {
        modifiers.metaKey = true;
      } else {
        mainKey = key;
      }
    }

    // Create and dispatch the keyboard event
    const event = new KeyboardEvent('keydown', {
      ...keyEvent,
      ...modifiers,
      key: mainKey,
      code: this.getKeyCode(mainKey)
    });

    // Dispatch on window for global shortcuts to be caught
    window.dispatchEvent(event);

    // Also dispatch keyup
    const upEvent = new KeyboardEvent('keyup', {
      ...keyEvent,
      ...modifiers,
      key: mainKey,
      code: this.getKeyCode(mainKey)
    });
    window.dispatchEvent(upEvent);

    // Small delay after shortcut
    await this.wait(100);
  }

  /**
   * Type text character by character
   */
  private async typeText(text: string): Promise<void> {
    if (!text) return;

    console.log('[TourKeyboardAutomation] Typing text:', text);

    const activeElement = document.activeElement as HTMLInputElement;
    
    // If active element is an input, set value directly
    if (activeElement && (activeElement.tagName === 'INPUT' || activeElement.tagName === 'TEXTAREA')) {
      activeElement.value = text;
      
      // Trigger input event
      const inputEvent = new Event('input', { bubbles: true, cancelable: true });
      activeElement.dispatchEvent(inputEvent);
      
      // Trigger change event
      const changeEvent = new Event('change', { bubbles: true, cancelable: true });
      activeElement.dispatchEvent(changeEvent);
    } else {
      // Otherwise dispatch keyboard events for each character
      for (const char of text) {
        const keyEvent: KeyboardEventInit = {
          bubbles: true,
          cancelable: true,
          composed: true,
          key: char
        };

        const keydownEvent = new KeyboardEvent('keydown', keyEvent);
        const keypressEvent = new KeyboardEvent('keypress', keyEvent);
        const keyupEvent = new KeyboardEvent('keyup', keyEvent);

        document.activeElement?.dispatchEvent(keydownEvent);
        document.activeElement?.dispatchEvent(keypressEvent);
        document.activeElement?.dispatchEvent(keyupEvent);

        // Small delay between characters
        await this.wait(50);
      }
    }
  }

  /**
   * Get key code for a key
   */
  private getKeyCode(key: string): string {
    const keyCodeMap: { [key: string]: string } = {
      'a': 'KeyA', 'b': 'KeyB', 'c': 'KeyC', 'd': 'KeyD', 'e': 'KeyE',
      'f': 'KeyF', 'g': 'KeyG', 'h': 'KeyH', 'i': 'KeyI', 'j': 'KeyJ',
      'k': 'KeyK', 'l': 'KeyL', 'm': 'KeyM', 'n': 'KeyN', 'o': 'KeyO',
      'p': 'KeyP', 'q': 'KeyQ', 'r': 'KeyR', 's': 'KeyS', 't': 'KeyT',
      'u': 'KeyU', 'v': 'KeyV', 'w': 'KeyW', 'x': 'KeyX', 'y': 'KeyY',
      'z': 'KeyZ',
      '0': 'Digit0', '1': 'Digit1', '2': 'Digit2', '3': 'Digit3', '4': 'Digit4',
      '5': 'Digit5', '6': 'Digit6', '7': 'Digit7', '8': 'Digit8', '9': 'Digit9',
      '[': 'BracketLeft', ']': 'BracketRight',
      '\\': 'Backslash', '/': 'Slash',
      '?': 'Slash', // Shift+/
      '\'': 'Quote',
      '"': 'Quote', // Shift+'
      ',': 'Comma',
      '<': 'Comma', // Shift+,
      '.': 'Period',
      '>': 'Period', // Shift+.
      'Escape': 'Escape',
      'Enter': 'Enter',
      'Tab': 'Tab',
      'Space': 'Space',
      'ArrowUp': 'ArrowUp',
      'ArrowDown': 'ArrowDown',
      'ArrowLeft': 'ArrowLeft',
      'ArrowRight': 'ArrowRight'
    };

    return keyCodeMap[key.toLowerCase()] || keyCodeMap[key] || key;
  }

  /**
   * Wait for a duration
   */
  private wait(ms: number): Promise<void> {
    return new Promise(resolve => setTimeout(resolve, ms));
  }
}
